import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserHistory, getUserPayments } from '../api';
import { ArrowLeft, ArrowRight, Clock, Star, Download, ChevronUp, ChevronDown, CreditCard, Zap } from 'lucide-react';

const UserHistory = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('optimization');

    // Optimization history state
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

    // Payment history state
    const [payments, setPayments] = useState([]);
    const [loadingPayments, setLoadingPayments] = useState(true);
    const [paymentSortConfig, setPaymentSortConfig] = useState({ key: 'created_at', direction: 'desc' });

    useEffect(() => {
        fetchHistory();
        fetchPayments();
    }, [id]);

    const fetchHistory = async () => {
        setLoadingHistory(true);
        try {
            const res = await getUserHistory(id);
            if (res.data.success) {
                setHistory(res.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch history", error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const fetchPayments = async () => {
        setLoadingPayments(true);
        try {
            const res = await getUserPayments(id);
            if (res.data.success) {
                setPayments(res.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch payments", error);
        } finally {
            setLoadingPayments(false);
        }
    };

    const sortedHistory = useMemo(() => {
        if (!history.length) return history;
        const sorted = [...history].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            if (aVal == null) aVal = '';
            if (bVal == null) bVal = '';
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }
            aVal = String(aVal).toLowerCase();
            bVal = String(bVal).toLowerCase();
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [history, sortConfig]);

    const sortedPayments = useMemo(() => {
        if (!payments.length) return payments;
        const sorted = [...payments].sort((a, b) => {
            let aVal = a[paymentSortConfig.key];
            let bVal = b[paymentSortConfig.key];
            if (aVal == null) aVal = '';
            if (bVal == null) bVal = '';
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return paymentSortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }
            aVal = String(aVal).toLowerCase();
            bVal = String(bVal).toLowerCase();
            if (aVal < bVal) return paymentSortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return paymentSortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [payments, paymentSortConfig]);

    const requestSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const requestPaymentSort = (key) => {
        setPaymentSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const renderSortIcon = (key, config) => {
        const isActive = config.key === key;
        return (
            <div className="flex flex-col ml-1">
                <ChevronUp size={12} className={isActive && config.direction === 'asc' ? 'text-blue-600' : 'text-gray-300'} />
                <ChevronDown size={12} className={`-mt-1 ${isActive && config.direction === 'desc' ? 'text-blue-600' : 'text-gray-300'}`} />
            </div>
        );
    };

    const formatBytes = (bytes, decimals = 2) => {
        if (!+bytes) return '0 B';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
    };

    const calculateReduction = (orig, opt) => {
        if (!orig || !opt) return 0;
        return ((orig - opt) / orig * 100).toFixed(1);
    };

    const ComparisonCell = ({ orig, opt, formatFn, showBadge = true }) => {
        const reduction = calculateReduction(orig, opt);
        const isImproved = reduction > 0;
        return (
            <div className="flex flex-col items-center justify-center space-y-1">
                <div className="flex items-center space-x-2 text-sm">
                    <span className="text-gray-500">{formatFn ? formatFn(orig) : orig?.toLocaleString()}</span>
                    <ArrowRight size={14} className="text-gray-400" />
                    <span className="font-semibold text-gray-900">{formatFn ? formatFn(opt) : opt?.toLocaleString()}</span>
                </div>
                {showBadge && opt && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isImproved ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {isImproved ? `▼ ${reduction}%` : 'No Change'}
                    </span>
                )}
            </div>
        );
    };

    const getPaymentTypeBadge = (type) => {
        if (type === 'subscription') return 'bg-purple-100 text-purple-700';
        if (type === 'credit') return 'bg-blue-100 text-blue-700';
        return 'bg-gray-100 text-gray-600';
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center space-x-4">
                <button
                    onClick={() => navigate('/users')}
                    className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                >
                    <ArrowLeft size={24} className="text-gray-600" />
                </button>
                <h1 className="text-2xl font-bold text-gray-800">User Details</h1>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
                <div className="flex space-x-4">
                    <button
                        onClick={() => setActiveTab('optimization')}
                        className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'optimization'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <Zap size={18} />
                        <span>Optimization History ({history.length})</span>
                    </button>
                    <button
                        onClick={() => setActiveTab('payment')}
                        className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'payment'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                    >
                        <CreditCard size={18} />
                        <span>Payment History ({payments.length})</span>
                    </button>
                </div>
            </div>

            {/* Optimization History Tab */}
            {activeTab === 'optimization' && (
                <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-100">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th onClick={() => requestSort('created_at')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                        <div className="flex items-center">Date{renderSortIcon('created_at', sortConfig)}</div>
                                    </th>
                                    <th onClick={() => requestSort('filename')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                        <div className="flex items-center">File Info{renderSortIcon('filename', sortConfig)}</div>
                                    </th>
                                    <th onClick={() => requestSort('orig_vertex')} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                        <div className="flex items-center justify-center">Vertices{renderSortIcon('orig_vertex', sortConfig)}</div>
                                    </th>
                                    <th onClick={() => requestSort('orig_polygon')} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                        <div className="flex items-center justify-center">Polygons{renderSortIcon('orig_polygon', sortConfig)}</div>
                                    </th>
                                    <th onClick={() => requestSort('orig_file_size')} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                        <div className="flex items-center justify-center">File Size{renderSortIcon('orig_file_size', sortConfig)}</div>
                                    </th>
                                    <th onClick={() => requestSort('rating')} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                        <div className="flex items-center justify-center">Rating / DL{renderSortIcon('rating', sortConfig)}</div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Processing Time</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loadingHistory ? (
                                    <tr><td colSpan="7" className="px-6 py-4 text-center text-gray-500">Loading...</td></tr>
                                ) : sortedHistory.length === 0 ? (
                                    <tr><td colSpan="7" className="px-6 py-4 text-center text-gray-500">No optimization history found.</td></tr>
                                ) : (
                                    sortedHistory.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.created_at?.split(' ')[0]}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-900">{item.filename}</span>
                                                    <div className="mt-1 text-xs text-gray-400 flex items-center">
                                                        <span className="uppercase">{item.orig_format}</span>
                                                        <ArrowRight size={10} className="mx-1" />
                                                        <span className="uppercase">{item.opt_format}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <ComparisonCell orig={item.orig_vertex} opt={item.opt_vertex} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <ComparisonCell orig={item.orig_polygon} opt={item.opt_polygon} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <ComparisonCell orig={item.orig_file_size} opt={item.opt_file_size} formatFn={formatBytes} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                                <div className="flex flex-col items-center space-y-1">
                                                    {item.rating ? (
                                                        <div className="flex items-center text-yellow-500 space-x-1">
                                                            <Star size={14} fill="currentColor" />
                                                            <span className="text-sm font-medium">{item.rating}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">-</span>
                                                    )}
                                                    <div className="flex items-center text-gray-500 space-x-1">
                                                        <Download size={14} />
                                                        <span className="text-xs">{item.download_count || 0}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col text-sm text-gray-600 space-y-1">
                                                    <div className="flex items-center space-x-2">
                                                        <span className="w-16 text-xs font-medium text-gray-400">Analysis</span>
                                                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">{item.analysis_time || '-'}</span>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <span className="w-16 text-xs font-medium text-gray-400">Optimize</span>
                                                        <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded text-xs">{item.optimization_time || '-'}</span>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Payment History Tab */}
            {activeTab === 'payment' && (
                <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-100">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th onClick={() => requestPaymentSort('created_at')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                        <div className="flex items-center">Date{renderSortIcon('created_at', paymentSortConfig)}</div>
                                    </th>
                                    <th onClick={() => requestPaymentSort('payment_type')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                        <div className="flex items-center">Type{renderSortIcon('payment_type', paymentSortConfig)}</div>
                                    </th>
                                    <th onClick={() => requestPaymentSort('price')} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                        <div className="flex items-center justify-end">Amount{renderSortIcon('price', paymentSortConfig)}</div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                                    <th onClick={() => requestPaymentSort('billing_type')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
                                        <div className="flex items-center">Billing Type{renderSortIcon('billing_type', paymentSortConfig)}</div>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expired At</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loadingPayments ? (
                                    <tr><td colSpan="6" className="px-6 py-4 text-center text-gray-500">Loading...</td></tr>
                                ) : sortedPayments.length === 0 ? (
                                    <tr><td colSpan="6" className="px-6 py-4 text-center text-gray-500">No payment history found.</td></tr>
                                ) : (
                                    sortedPayments.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.created_at?.split(' ')[0]}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 py-1 rounded text-xs font-semibold ${getPaymentTypeBadge(item.payment_type)}`}>
                                                    {item.payment_type === 'subscription' ? 'Subscription' : item.payment_type === 'credit' ? 'Credit Pack' : item.payment_type}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                                                {item.price_display}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {item.reason || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {item.billing_type || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {item.expired_at?.split(' ')[0] || '-'}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserHistory;
